import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ModalCloseX from "../../components/common/ModalCloseX";
import InfoTooltip from "../../components/common/InfoTooltip";
import ReportHelpCard from "../../components/reports/ReportHelpCard";
import useHelpGuideNavigation from "../../hooks/useHelpGuideNavigation";
import { useAuth } from "../../context/AuthContext";
import { getCurrentSalonId } from "../../utils/currentSalon";
import { getSalonTimezone } from "../../utils/salonTimezone";
import { getFeedbackThemesReport } from "../../api/clientReviews";
import { REPORT_HELP } from "../../constants/reportHelpContent";
import "../ReportsSectionCompact.css";

const formatDateISO = (date, timeZone) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  if (timeZone) {
    try {
      const formatter = new Intl.DateTimeFormat("en-CA", {
        timeZone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
      return formatter.format(date);
    } catch (err) {
      // fallback
    }
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const addDays = (base, days) => {
  const next = new Date(base.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

const csvEscape = (value) => {
  if (value == null) return "";
  const str = String(value);
  if (str.includes(",") || str.includes("\"") || str.includes("\n")) {
    return `"${str.replace(/"/g, "\"\"")}"`;
  }
  return str;
};

const downloadCsv = (filename, headers, rows) => {
  const csv = [
    headers.map(csvEscape).join(","),
    ...rows.map((row) => row.map(csvEscape).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export default function ComplaintThemesReport() {
  const navigate = useNavigate();
  const { user, currentSalon } = useAuth();
  const { jumpToSectionWithHighlight, getSectionClassName } = useHelpGuideNavigation();
  const helpGuide = REPORT_HELP.COMPLAINT_THEMES;
  const salonId = getCurrentSalonId(user);
  const salonTimezone = useMemo(() => getSalonTimezone(currentSalon), [currentSalon]);
  const today = formatDateISO(new Date(), salonTimezone);
  const defaultStart = formatDateISO(addDays(new Date(), -29), salonTimezone);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(today);
  const [bucket, setBucket] = useState("week");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadReport = useCallback(async () => {
    if (!salonId) {
      setError("Select a salon to view this report.");
      return;
    }
    if (!startDate || !endDate) {
      setError("Select a valid start and end date.");
      return;
    }
    if (startDate > endDate) {
      setError("End date can't be before start date.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const data = await getFeedbackThemesReport({
        salon_id: salonId,
        start_date: startDate,
        end_date: endDate,
        bucket,
      });
      setReport(data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Unable to load report data.");
      setReport(null);
    } finally {
      setLoading(false);
    }
  }, [bucket, endDate, salonId, startDate]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleExport = () => {
    if (!report) return;
    const headers = ["Theme", "Section", "Key", "Label", "Count"];
    const rows = [];
    (report.top_themes || []).forEach((row) => {
      rows.push([row.theme, "Top themes", row.theme, row.theme, row.count]);
    });
    (report.by_category || []).forEach((row) => {
      rows.push([row.theme, "Category", row.key, row.label, row.count]);
    });
    (report.by_staff || []).forEach((row) => {
      rows.push([row.theme, "Staff", row.key, row.label, row.count]);
    });
    (report.by_channel || []).forEach((row) => {
      rows.push([row.theme, "Channel", row.key, row.label, row.count]);
    });
    (report.by_day_of_week || []).forEach((row) => {
      rows.push([row.theme, "Day of week", row.key, row.label, row.count]);
    });
    (report.trend || []).forEach((row) => {
      rows.push([row.theme, "Trend", row.bucket_label, row.bucket_label, row.count]);
    });
    downloadCsv("complaint-themes.csv", headers, rows);
  };

  const topThemes = Array.isArray(report?.top_themes) ? report.top_themes : [];
  const byCategory = Array.isArray(report?.by_category) ? report.by_category : [];
  const byStaff = Array.isArray(report?.by_staff) ? report.by_staff : [];
  const byChannel = Array.isArray(report?.by_channel) ? report.by_channel : [];
  const byDay = Array.isArray(report?.by_day_of_week) ? report.by_day_of_week : [];
  const trend = Array.isArray(report?.trend) ? report.trend : [];

  const renderTable = (rows, label) => (
    <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
      <table className="w-full table-fixed text-sm">
        <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="px-3 py-2 text-left w-40">Theme</th>
            <th className="px-3 py-2 text-left">{label}</th>
            <th className="px-3 py-2 text-right w-24">Count</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {rows.length === 0 && (
            <tr>
              <td className="px-3 py-4 text-slate-500" colSpan={3}>
                No data available.
              </td>
            </tr>
          )}
          {rows.map((row, idx) => (
            <tr key={`${row.theme}-${row.key}-${idx}`} className="hover:bg-slate-50">
              <td className="px-3 py-3 text-slate-700">{row.theme}</td>
              <td className="px-3 py-3 text-slate-700">{row.label}</td>
              <td className="px-3 py-3 text-right text-slate-700">
                {row.count}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="reportSectionShell min-h-screen bg-slate-100">
      <div className="reportSectionWide report-section-spacious report-section-compact">
        <div className="rounded-3xl bg-white shadow-2xl overflow-hidden">
          <div className="bg-rose-500 px-6 py-4 flex items-center justify-between gap-4 relative">
            <div className="text-xl md:text-2xl font-semibold text-white">
              Complaint Themes
            </div>
            <div className="absolute right-4 top-3 z-50">
              <ModalCloseX onClick={() => navigate("/manager/reports")} />
            </div>
          </div>
          <div className="px-6 py-6 space-y-6 text-sm md:text-base">
            <div
              id="complaint-themes-overview"
              className={getSectionClassName("complaint-themes-overview")}
            >
              <ReportHelpCard
                title={helpGuide.title}
                subtitle={helpGuide.subtitle}
                sections={helpGuide.sections}
                troubleshooting={helpGuide.troubleshooting}
                onJumpToSection={jumpToSectionWithHighlight}
              />
            </div>

            <div
              id="complaint-themes-filters"
              className={`rounded-2xl border border-slate-200 bg-white p-4 space-y-4 ${getSectionClassName(
                "complaint-themes-filters"
              )}`}
            >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="text-base md:text-lg font-semibold text-slate-900">
                    Filters
                  </div>
                  <div className="text-sm text-slate-500">
                    All dates use the salon timezone.
                  </div>
                </div>
                <div
                  id="complaint-themes-export"
                  className={`flex flex-wrap gap-2 ${getSectionClassName(
                    "complaint-themes-export"
                  )}`}
                >
                  <button
                    type="button"
                    onClick={loadReport}
                    className="rounded-full bg-slate-900 px-4 py-2 text-xs md:text-sm font-semibold text-white hover:bg-slate-800"
                  >
                    Run report
                  </button>
                  <button
                    type="button"
                    onClick={handleExport}
                    className="rounded-full border border-slate-300 px-4 py-2 text-xs md:text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  >
                    Export CSV
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Start date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(event) => setStartDate(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    End date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(event) => setEndDate(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold uppercase text-slate-500">
                    Bucket
                  </label>
                  <select
                    value={bucket}
                    onChange={(event) => setBucket(event.target.value)}
                    className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  >
                    <option value="day">Day</option>
                    <option value="week">Week</option>
                    <option value="month">Month</option>
                  </select>
                </div>
              </div>
            </div>

            {error && (
              <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-rose-700 text-sm md:text-base">
                {error}
              </div>
            )}
            {loading && (
              <div className="text-sm text-slate-500">Loading report...</div>
            )}

            {!loading && report && (
              <div
                id="complaint-themes-details"
                className={`space-y-6 ${getSectionClassName("complaint-themes-details")}`}
              >
                <div
                  id="complaint-themes-kpis"
                  className={`rounded-2xl border border-slate-200 bg-white p-5 ${getSectionClassName(
                    "complaint-themes-kpis"
                  )}`}
                >
                  <div className="flex items-center gap-2 text-base md:text-lg font-semibold text-slate-900">
                    Top Themes
                    <InfoTooltip text="Most frequent themes during the selected range." />
                  </div>
                  <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
                    {topThemes.length === 0 && (
                      <div className="text-sm text-slate-500">
                        No theme data for this range.
                      </div>
                    )}
                    {topThemes.map((row) => (
                      <div
                        key={row.theme}
                        className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
                      >
                        <div className="text-xs uppercase tracking-wide text-slate-500">
                          {row.theme.replace(/_/g, " ")}
                        </div>
                        <div className="mt-2 text-2xl font-semibold text-slate-900">
                          {row.count}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {renderTable(byCategory, "Category")}
                  {renderTable(byStaff, "Staff")}
                </div>
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  {renderTable(byChannel, "Channel")}
                  {renderTable(byDay, "Day of week")}
                </div>

                <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
                  <table className="w-full table-fixed text-sm">
                    <thead className="bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-3 py-2 text-left w-40">Theme</th>
                        <th className="px-3 py-2 text-left">Bucket</th>
                        <th className="px-3 py-2 text-right w-24">Count</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {trend.length === 0 && (
                        <tr>
                          <td className="px-3 py-4 text-slate-500" colSpan={3}>
                            No trend data.
                          </td>
                        </tr>
                      )}
                      {trend.map((row, idx) => (
                        <tr key={`${row.theme}-${row.bucket_label}-${idx}`}>
                          <td className="px-3 py-3 text-slate-700">{row.theme}</td>
                          <td className="px-3 py-3 text-slate-700">
                            {row.bucket_label}
                          </td>
                          <td className="px-3 py-3 text-right text-slate-700">
                            {row.count}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
